import {assertNotNil, isEqual, isNil} from "app/utils/stdlib";
import {createContext, type ReactNode, useCallback, useContext, useMemo} from "react";
import {useSafePathParams, zControlCardInstance} from "app/components/hooks/useSafeParams";
import {useEmployeeClientId} from "app/employee/state/bootstrapEmployee/selectors";
import type {
  ColumnFragment,
  ControlCardInstanceFullFragment,
  ControlCardInstanceIdFragment,
  ControlCardInstanceUpdatedValueFragment
} from "app/gql/graphqlSchema";
import {useControlCardInstance} from "app/employee/controlCard/hook/useControlCardInstance";
import type {ID, MaybeNil} from "app/utils/types";
import {useIonViewWillEnter} from "@ionic/react";
import type {Refetch} from "app/employee/controlCard/hook/useQuery";
import {ApiState} from "app/state/common/loadable";
import {LoadingLoadable} from "app/employee/Loading";
import {usePermissions} from "app/employee/hooks/usePermissions";
import {useRemount} from "app/employee/controlCard/hook/useRemount";
import type {Updater} from "use-immer";

interface ControlCardInstanceContextValue {
  loadingState: ApiState;
  setInstance: Updater<ControlCardInstanceFullFragment>;
  instance: ControlCardInstanceFullFragment;
  rowId: MaybeNil<ID>;
  primaryColumn: ColumnFragment;
  isComplete: boolean;
  isRowComplete: (rowId: MaybeNil<ID>) => boolean;
  updateValues: (newValues: ControlCardInstanceUpdatedValueFragment[]) => void;
  refresh: Refetch;
  canEdit: boolean;
  isTransposedCard: boolean;
}

const Context = createContext<ControlCardInstanceContextValue | null>(null);

export const ControlCardInstanceContext = ({children}: {children: ReactNode}) => {
  const {canEditControlCardLocation} = usePermissions();
  const {instanceId, rowId} = useSafePathParams(zControlCardInstance);
  const remountKey = useRemount();
  const clientId = useEmployeeClientId();

  const controlCardInstanceId = useMemo(
    (): ControlCardInstanceIdFragment => ({instanceId, clientId}),
    [instanceId, clientId]
  );

  const {setData, refetch, fetch, data} = useControlCardInstance();
  const instance = data.value;
  const isRowComplete = useCallback(
    (rowId: MaybeNil<ID>) =>
      !isNil(rowId) && (instance?.rows.filter((r) => rowId === r.rowId).every((r) => !isNil(r.completedBy)) ?? false),
    [instance?.rows]
  );

  const isComplete = useMemo(
    () => !isNil(instance?.completedAt) || isRowComplete(rowId),
    [instance?.completedAt, isRowComplete, rowId]
  );

  const updateValues = useCallback(
    (newValues: ControlCardInstanceUpdatedValueFragment[]) => {
      setData((draft) => {
        NEXT_VALUE: for (const newValue of newValues) {
          for (const row of draft.rows) {
            for (const valueInstance of row.columns) {
              if (valueInstance.id === newValue.valueId) {
                valueInstance.value = newValue.value;
                valueInstance.validationResult = newValue.validationResult;
                valueInstance.overriddenValidationResult = newValue.overriddenValidationResult ?? null;
                valueInstance.show = newValue.show;
                continue NEXT_VALUE;
              }
            }
          }
        }
      });
    },
    [setData]
  );

  const contextValue = useMemo((): ControlCardInstanceContextValue | null => {
    if (!isNil(instance)) {
      const definition = instance.definition;
      const primaryColumnKey = definition.primaryColumnKey;
      const primaryColumn = definition.columns.find((c) => isEqual(c.key, primaryColumnKey));
      assertNotNil(primaryColumn);
      const isTransposedCard = instance.isTransposed && instance.rows.length === 1;

      const canEdit = canEditControlCardLocation(instance.location.id, instance.date);
      return {
        refresh: refetch,
        rowId,
        primaryColumn,
        instance,
        isComplete,
        isRowComplete,
        setInstance: setData,
        loadingState: data.loadingState,
        updateValues,
        canEdit,
        isTransposedCard
      };
    } else {
      return null;
    }
  }, [
    canEditControlCardLocation,
    instance,
    data.loadingState,
    isComplete,
    isRowComplete,
    refetch,
    rowId,
    setData,
    updateValues
  ]);

  useIonViewWillEnter(() => {
    fetch({id: controlCardInstanceId});
  }, [fetch, controlCardInstanceId]);

  return (
    <>
      <LoadingLoadable loadable={data} />
      {!isNil(contextValue) && (
        <Context.Provider key={remountKey} value={contextValue}>
          {children}
        </Context.Provider>
      )}
    </>
  );
};

export const useControlCardInstanceContext = () => {
  const context = useContext(Context);
  assertNotNil(context);
  return context;
};
